"use client";

import { useState } from "react";
import {
  ArrowLeft,
  ChevronRight,
  Users,
  Star,
  BadgeCheck,
  Briefcase,
  AlertTriangle,
  BookOpen,
} from "lucide-react";
import { getMentorBySlug } from "@/data/mentores";
import type { Mentor } from "@/types/mentor";
import Link from "next/link";

interface InstructorProfileProps {
  mentor: Mentor;
}

type TabId = "info" | "bio";

const TABS: { id: TabId; label: string }[] = [
  { id: "info", label: "Trayectoria" },
  { id: "bio", label: "Acerca de" },
];

// ── Not Found ─────────────────────────────────────────────────────────────────

function NotFound() {
  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 text-neutral-500">
      <AlertTriangle size={36} className="text-blue-400" />
      <h1 className="font-serif text-2xl text-neutral-900">Mentor no encontrado</h1>
      <a
        href="/nosotros/mentores"
        className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-neutral-700 hover:text-blue-500 transition-colors"
      >
        <ArrowLeft size={13} />
        Volver al listado
      </a>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function InstructorProfile({ mentor }: InstructorProfileProps) {
  const [tab, setTab] = useState<TabId>("info");

  //const mentor: Mentor | undefined = getMentorBySlug(slug);
  if (!mentor) return <NotFound />;

  const firstName = mentor.nombre.split(" ")[0];

  return (
    <div className="bg-white text-neutral-900">

      {/* ── Breadcrumb ── */}
      <nav
        aria-label="Breadcrumb"
        className="border-b border-neutral-200 h-11 flex items-center gap-2 px-5 text-xs text-neutral-400"
      >
        <a
          href="/nosotros/mentores"
          className="flex items-center gap-1.5 font-medium text-neutral-500 hover:text-neutral-900 transition-colors"
        >
          <ArrowLeft size={13} />
          Ir a mentores
        </a>
        <ChevronRight size={12} className="text-neutral-300" />
        <span className="text-neutral-800 font-medium" aria-current="page">
          {mentor.nombre}
        </span>
      </nav>

      {/* ── HERO: split layout ── */}
      <section className="bg-neutral-950 relative overflow-hidden">

        {/* Glow azul */}
        <div className="pointer-events-none absolute top-0 right-0 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[100px]" />
        <div className="pointer-events-none absolute bottom-0 left-1/2 w-[300px] h-[300px] bg-blue-500/5 rounded-full blur-[80px]" />

        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[90vh]">

          {/* ── Columna imagen ── */}
          <div className="relative w-full h-[70vw] max-h-[520px] lg:max-h-none lg:h-auto bg-gradient-to-b from-neutral-100 to-neutral-300">

            <img
              src={mentor.perfil}
              alt={`Foto de ${mentor.nombre}`}
              className="w-full h-full object-cover object-top"
              onError={(e) => {
                e.currentTarget.style.display = "none";
                (e.currentTarget.nextElementSibling as HTMLElement)?.removeAttribute("hidden");
              }}
            />

            {/* fallback */}
            {/* <div
              hidden
              className="w-full h-full bg-neutral-800 flex items-center justify-center"
            >
              <span className="font-serif text-[8rem] text-white/5 select-none">
                {mentor.nombre.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
              </span>
            </div> */}

            {/* Gradiente derecho — fusiona con la columna oscura en desktop */}
            <div className="hidden lg:block absolute inset-y-0 right-0 w-32 bg-gradient-to-r from-transparent to-neutral-950" />

            {/* Gradiente inferior — para mobile */}
            <div className="lg:hidden absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-neutral-950 to-transparent" />

            {/* Badge */}
            {/* <span className="absolute top-4 left-4 flex items-center gap-1.5 bg-blue-500 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-sm shadow-lg">
              <BadgeCheck size={11} />
              {mentor.rango}
            </span> */}
          </div>

          {/* ── Columna texto ── */}
          <div className="flex flex-col justify-center px-6 py-10 md:px-10 lg:px-14 xl:px-16">

            {/* <p className="text-blue-400 text-[11px] font-semibold uppercase tracking-[0.22em] mb-3">
              Te presentamos a
            </p> */}

            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.05] mb-4">
              {mentor.nombre}
            </h1>

            {/* Separador azul */}
            <div className="w-12 h-0.5 bg-blue-500 mb-5" />

            {/* Información principal — visible directamente */}
            <p className="text-white/60 text-md leading-relaxed mb-8 max-w-md">
              {mentor.informacion}
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-px bg-white/10 rounded-md overflow-hidden mb-8 w-full max-w-sm">
              {[
                { icon: <Users size={12} />, value: mentor.stats.estudiantes.toLocaleString("es-PE"), label: "Estudiantes" },
                { icon: <Briefcase size={12} />, value: `${mentor.stats.exp} años`, label: "Experiencia" },
                { icon: <Star size={12} />, value: "5.0", label: "Valoración" },
              ].map((s) => (
                <div key={s.label} className="flex flex-col items-center py-4 px-2 bg-neutral-900">
                  <div className="flex items-center gap-1 text-white/30 mb-1.5">
                    {s.icon}
                    <span className="text-[9px] uppercase tracking-widest font-semibold">{s.label}</span>
                  </div>
                  <span className="font-serif text-xl text-white leading-none">{s.value}</span>
                </div>
              ))}
            </div>

            {/* CTAs */}
            {/* <div className="flex flex-wrap gap-3">
              <button
                type="button"
                className="bg-blue-500 text-white font-bold text-xs uppercase tracking-widest px-6 py-3 rounded-sm hover:bg-blue-400 transition-colors"
              >
                Ver programas
              </button>
              <button
                type="button"
                className="border border-white/20 text-white/60 font-semibold text-xs uppercase tracking-widest px-6 py-3 rounded-sm hover:border-white/40 hover:text-white transition-colors"
              >
                Conocer más
              </button>
            </div> */}
          </div>

        </div>
      </section>

      {/* ── Sección inferior ── */}
      <div className="max-w-5xl mx-auto px-5 md:px-10 py-14 grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-10 items-start">

        {/* Main con tabs */}
        <main>
          <div className="flex border-b-2 border-neutral-200 mb-8" role="tablist">
            {TABS.map(({ id, label }) => (
              <button
                key={id}
                role="tab"
                aria-selected={tab === id}
                onClick={() => setTab(id)}
                className={`relative px-5 py-3 text-xs font-semibold uppercase tracking-widest transition-colors ${tab === id ? "text-neutral-900" : "text-neutral-400 hover:text-neutral-600"
                  }`}
              >
                {label}
                {tab === id && (
                  <span className="absolute bottom-[-2px] left-0 right-0 h-0.5 bg-blue-500" />
                )}
              </button>
            ))}
          </div>

          {tab === "info" && (
            <div role="tabpanel">
              <h2 className="font-serif text-2xl font-bold mb-4">
                Trayectoria de{" "}
                <span className="italic text-neutral-400">{mentor.nombre}</span>
              </h2>
              <div className="w-10 h-0.5 bg-blue-500 mb-5" />
              {/* Si hay biografía larga la usa; si no, cae en informacion */}
              {(mentor.biografia ?? mentor.informacion)
                .split("\n\n")
                .map((parrafo, i) => (
                  <p
                    key={i}
                    className="text-neutral-500 leading-relaxed font-light text-[15px] mb-4 last:mb-0"
                  >
                    {parrafo}
                  </p>
                ))}
            </div>
          )}

          {tab === "bio" && (
            <div role="tabpanel">
              <h2 className="font-serif text-2xl font-bold mb-4">
                Acerca de <span className="italic text-neutral-400">{mentor.nombre}</span>
              </h2>
              <div className="w-10 h-0.5 bg-blue-500 mb-5" />
              <p className="text-neutral-500 leading-relaxed font-light text-[15px]">
                {mentor.bio}
              </p>
            </div>
          )}
        </main>

        {/* Sidebar */}
        <aside className="flex flex-col gap-4">

          {/* CTA card */}
          <div className="bg-neutral-950 rounded-md p-6">
            <h3 className="font-serif text-xl text-white leading-snug mb-2">
              ¿Quieres aprender con {firstName}?
            </h3>
            <p className="text-white/40 text-xs leading-relaxed mb-5">
              Descubre los programas disponibles y comienza tu camino en la nueva economía.
            </p>
            <Link
              href="/cursos-udaxen"
              className="block w-full text-center bg-blue-500 text-white font-bold text-xs uppercase tracking-widest py-3 rounded-sm hover:bg-blue-400 transition-colors"
            >
              Ver programas
            </Link>
          </div>

          {/* Rating */}
          <div className="border border-neutral-200 rounded-md p-5 bg-white flex gap-4 items-center">
            <span className="font-serif text-5xl font-bold leading-none">5.0</span>
            <div>
              <div className="flex gap-0.5 mb-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={13} className="fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-[11px] text-neutral-400">
                {mentor.stats.estudiantes.toLocaleString("es-PE")} estudiantes
              </p>
            </div>
          </div>

          {/* Meta */}
          {/* <div className="border border-neutral-200 rounded-md p-5 bg-white">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-neutral-400 mb-3">
              Datos
            </p>
            <dl className="flex flex-col gap-2.5 text-xs">
              {[
                { dt: "Rango", dd: mentor.rango },
                { dt: "Experiencia", dd: `${mentor.stats.exp} años` },
                { dt: "Slug", dd: `/${mentor.slug}` },
              ].map(({ dt, dd }) => (
                <div key={dt} className="flex justify-between gap-2">
                  <dt className="text-neutral-400 font-medium shrink-0">{dt}</dt>
                  <dd className="text-neutral-700 font-mono text-right truncate">{dd}</dd>
                </div>
              ))}
            </dl>
          </div> */}
        </aside>
      </div>
    </div>
  );
}